import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { AddToCollectionButton } from '@modules/feedback/components/AddToCollectionButton';
import {
  Box,
  CircularProgress,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { SyntheticEvent, useContext, useState } from 'react';
import { CollectionItem } from '../../../types';
import { RemoveFromCollectionButton } from '../../RemoveFromCollectionButton';
import { OpenInCollectionLink } from '../OpenInCollectionLink';
import { SaveFeedbackContext } from '../SaveFeedbackContext';
import {
  useAddFeedbackToCollection,
  useRemoveFeedbackFromCollection,
} from '../hooks';
import { isSnippetKeywordSummary } from '../utils';

interface CollectionListItemProps {
  collectionItem: CollectionItem;
  feedbackID: string;
}

export const CollectionListItem = ({
  collectionItem,
  feedbackID,
}: CollectionListItemProps) => {
  const [isItemHovered, setIsItemHovered] = useState(false);
  const {
    id,
    title,
    editToken,
    eqlExpression,
    savedFeedbackMetadata,
    viewToken,
  } = collectionItem;
  const contextData = useContext(SaveFeedbackContext);
  const isSnippet = contextData?.snippetID;

  const isFeedbackPartOfCollection = isSnippet
    ? savedFeedbackMetadata?.some(
        ({ snippetMetadataV2, feedbackID: metadataFeedbackID }) =>
          snippetMetadataV2?.some(
            (snippet) =>
              isSnippetKeywordSummary(snippet.location) &&
              snippet?.location?.labelID === contextData.labelID &&
              snippet.location?.msgIdx === contextData.summaryIndex,
          ) && feedbackID === metadataFeedbackID,
      )
    : eqlExpression.includes(feedbackID);

  const { showSuccessNotification } = useNotificationContext();

  const { addFeedbackToCollection, isLoading: isAddingFeedbackToCollection } =
    useAddFeedbackToCollection({
      collectionEQL: eqlExpression,
      feedbackID,
      collectionID: id,
      collectionEditToken: editToken,
    });

  const {
    removeFeedbackFromCollection,
    isLoading: isRemovingFeedbackToCollection,
  } = useRemoveFeedbackFromCollection({
    collectionEQL: eqlExpression,
    feedbackID,
    collectionID: id,
    collectionEditToken: editToken,
    collectionTitle: title,
    collectionViewToken: viewToken,
  });

  const addToCollection = async (event: SyntheticEvent) => {
    event.stopPropagation();

    if (isAddingFeedbackToCollection || isFeedbackPartOfCollection) {
      return;
    }

    await addFeedbackToCollection();

    showSuccessNotification({
      contentComponent: `${
        isSnippet ? 'Snippet' : 'Feedback record'
      } added to "${title}"`,
      props: {
        severity: 'success',
        timeout: 15000,
        isClosable: true,
        action: <OpenInCollectionLink viewToken={viewToken ?? ''} />,
      },
    });
  };

  const removeFromCollection = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();

    await removeFeedbackFromCollection();
  };

  const renderPrimaryMenuButton = () => {
    if (isAddingFeedbackToCollection || isRemovingFeedbackToCollection) {
      return <CircularProgress size={20} />;
    }

    if (isItemHovered) {
      if (isFeedbackPartOfCollection) {
        return (
          <RemoveFromCollectionButton
            removeFromCollection={(event) => removeFromCollection(event)}
            isDisabled={!editToken}
          />
        );
      } else {
        return <AddToCollectionButton isDisabled={!editToken} />;
      }
    }
  };

  return (
    <MenuItem
      key={id}
      onClick={(event) => addToCollection(event)}
      sx={{
        color: isFeedbackPartOfCollection ? 'text.disabled' : 'text.primary',
        height: '100%',
      }}
      onMouseOver={() => setIsItemHovered(true)}
      onMouseOut={() => setIsItemHovered(false)}
      tabIndex={0}
    >
      <Stack
        justifyContent="space-between"
        alignItems="center"
        direction="row"
        width="100%"
      >
        <Typography noWrap sx={{ flex: 1 }}>
          {title}
        </Typography>
        <Box>{renderPrimaryMenuButton()}</Box>
      </Stack>
    </MenuItem>
  );
};
